import { useApolloClient } from '@apollo/client';
import StartStripeCheckoutMutation from '@apps/www/src/www/queries/StartStripeCheckoutMutation';
import StartStripeUpdateBillingMethodMutation from '@apps/www/src/www/queries/StartStripeUpdateBillingMethodMutation';
import UpdateBillingPlanMutation from '@apps/www/src/www/queries/UpdateBillingPlanMutation';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import PaymentButtonType from '@pkgs/shared/enums/PaymentButtonType';
import SubscriptionType from '@pkgs/shared/enums/SubscriptionType';
import { getSubscriptionTierAndBillingCycleKey, Plan } from '@pkgs/shared/plans';
import { useState } from 'react';

type UseStripeCheckoutProps = {
	buttonType: ValueOf<typeof PaymentButtonType>;
	type: ValueOf<typeof SubscriptionType>;
	plan: Plan | null;
	hasActivePlan: boolean;
};

export default function useStripeCheckout({
	buttonType,
	type,
	plan,
	hasActivePlan,
}: UseStripeCheckoutProps) {
	const client = useApolloClient();
	const [isLoading, setIsLoading] = useState(false);

	const handleStripeCheckout = useEventCallback(async () => {
		if (!plan) {
			return;
		}

		setIsLoading(true);

		try {
			let redirectURL: string | undefined | null = undefined;

			// Only update plan if user is already subscribed
			if (buttonType === PaymentButtonType.SUBSCRIBE && hasActivePlan) {
				const { data } = await client.mutate({
					mutation: UpdateBillingPlanMutation,
					variables: {
						input: {
							type,
							tierAndBillingCycle: getSubscriptionTierAndBillingCycleKey(plan),
						},
					},
				});

				redirectURL = data?.updateBillingPlan.redirectURL;

				// If successful, just redirect to billing page
				if (data && !redirectURL) {
					redirectURL = '/billing/';
				}
			} else if (buttonType === PaymentButtonType.EDIT_PAYMENT_METHOD) {
				const { data } = await client.mutate({
					mutation: StartStripeUpdateBillingMethodMutation,
					variables: {
						input: {
							type,
						},
					},
				});

				redirectURL = data?.startStripeUpdateBillingMethod.url;
			} else {
				// New subscription or renewal
				const { data } = await client.mutate({
					mutation: StartStripeCheckoutMutation,
					variables: {
						input: {
							type,
							planID: plan.stripePlanID,
						},
					},
				});

				redirectURL = data?.startStripeCheckout.url;
			}

			if (redirectURL) {
				const url = redirectURL;

				setTimeout(() => {
					window.location.href = url;
				});
				return;
			}
		} catch (e) {
			const error = e instanceof Error ? e : new Error('Unknown error');
			alert(error.message);
		}

		setIsLoading(false);
	});

	return {
		isLoading,
		handleStripeCheckout,
	};
}
